import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { ComposedChart, Line, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { Search, Filter, Calendar, Trophy, SortAsc, SortDesc, Share, Check, Pencil, Trash2, Globe, ChevronDown, ChevronUp, Square, CheckSquare, X, CloudOff } from 'lucide-react';
import { useOffline } from '../hooks/useOffline';
import { findScoringRuns, getServeRotationStats, isAce, isTeamAPoint, isTeamBPoint, isTimeoutEntry, scoringBoxLabel, scoringTeam } from '../utils/scoringOrder';
import './MatchDisplay.css';

const TEAM_A_COLOR = '#e11d48';
const TEAM_B_COLOR = '#2563eb';

function matchResult(match) {
  if (match.scoreA > match.scoreB) return 'win';
  if (match.scoreB > match.scoreA) return 'loss';
  return 'tie';
}

function resultLetter(result) {
  if (result === 'win') return 'W';
  if (result === 'loss') return 'L';
  return 'T';
}

function dayKeyFrom(dateValue) {
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return 'unknown';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function formatDayHeading(dateValue) {
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return 'Unknown date';
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const that = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diff = Math.round((today - that) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  });
}

function groupByDay(matches, getDate) {
  const groups = [];
  const index = new Map();
  matches.forEach((match) => {
    const key = dayKeyFrom(getDate(match));
    if (!index.has(key)) {
      const group = { key, date: getDate(match), matches: [] };
      index.set(key, group);
      groups.push(group);
    }
    index.get(key).matches.push(match);
  });
  return groups;
}

function rallyTickClass(score) {
  if (isTimeoutEntry(score)) return 'rally-tick timeout';
  const team = scoringTeam(score);
  if (!team) return 'rally-tick';
  return `rally-tick team-${team.toLowerCase()}${isAce(score) ? ' ace' : ''}`;
}

function MatchDisplay({ token, onEdit, nightMode, onSelectMatch, selectedMatches = [], onMatchClick }) {
  const { offlineMatches } = useOffline(token);

  const [matches, setMatches] = useState([]);
  const [showOfflineMatches, setShowOfflineMatches] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);

  const [searchTerm, setSearchTerm] = useState('');
  const [opponent, setOpponent] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [result, setResult] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showFilters, setShowFilters] = useState(false);
  const [teamSuggestions, setTeamSuggestions] = useState([]);
  const [copiedMatchId, setCopiedMatchId] = useState(null);
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const observerRef = useRef();
  const lastMatchElementRef = useCallback((node) => {
    if (isLoading) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore) {
        setPage((prevPage) => prevPage + 1);
      }
    });
    if (node) observerRef.current.observe(node);
  }, [isLoading, hasMore]);

  useEffect(() => {
    resetAndFetchMatches();
  }, [searchTerm, opponent, dateFrom, dateTo, result, sortBy, sortOrder]);

  useEffect(() => {
    const pending = offlineMatches.filter((match) => !match.synced);
    if (pending.length > 0) {
      setShowOfflineMatches(true);
    }
  }, [offlineMatches]);

  useEffect(() => {
    if (page > 1) {
      fetchMoreMatches();
    }
  }, [page]);

  const resetAndFetchMatches = () => {
    setMatches([]);
    setPage(1);
    setHasMore(true);
    fetchMatches(true);
  };

  const fetchMatches = async (reset = false) => {
    setIsLoading(true);
    try {
      const hasActiveFilters = searchTerm || opponent || dateFrom || dateTo || result !== 'all';
      const hasCustomSorting = sortBy !== 'date' || sortOrder !== 'desc';

      if (hasActiveFilters || hasCustomSorting) {
        const params = new URLSearchParams({
          page: reset ? '1' : page.toString(),
          limit: '20',
          search: searchTerm,
          opponent: opponent,
          dateFrom: dateFrom,
          dateTo: dateTo,
          result: result,
          sortBy: sortBy,
          sortOrder: sortOrder
        });

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/matches/search/advanced?${params}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (reset) {
          setMatches(response.data.matches);
        } else {
          setMatches((prev) => [...prev, ...response.data.matches]);
        }

        setHasMore(response.data.pagination.hasNext);
      } else {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/matches/all`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const responseData = response.data;
        const fetched = responseData.matches || responseData;
        const paginationData = responseData.pagination;
        const sortedMatches = paginationData ? fetched : fetched.sort((a, b) => new Date(b.date) - new Date(a.date));

        if (reset || page === 1) {
          setMatches(sortedMatches);
        } else {
          setMatches((prev) => [...prev, ...sortedMatches]);
        }

        setHasMore(paginationData ? paginationData.hasNext : false);
      }
    } catch (error) {
      console.error('Error fetching matches:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMoreMatches = () => {
    if (!isLoading && hasMore) {
      fetchMatches(false);
    }
  };

  const fetchTeamSuggestions = async (query) => {
    if (!query || query.length < 2) {
      setTeamSuggestions([]);
      return;
    }

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/matches/suggestions/teams?q=${query}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTeamSuggestions(response.data);
    } catch (error) {
      console.error('Failed to fetch team suggestions:', error);
    }
  };

  const isExpanded = (id) => expandedIds.has(id);

  const toggleExpanded = (id, event) => {
    event.stopPropagation();
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this match?')) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_URL}/api/matches/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        resetAndFetchMatches();
      } catch (error) {
        console.error('Error deleting match:', error);
        alert('Failed to delete match');
      }
    }
  };

  const generateShareId = async (matchId) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/matches/${matchId}/generateShareId`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data.shareId;
    } catch (error) {
      console.error('Error generating share ID:', error);
      throw error;
    }
  };

  const copyToClipboard = async (text) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (err) {
        console.warn('Clipboard API failed, trying fallback method');
      }
    }

    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      return true;
    } catch (err) {
      console.error('All clipboard methods failed:', err);
      return false;
    }
  };

  const handleShare = async (match) => {
    try {
      let shareId = match.shareId;

      if (!shareId) {
        shareId = await generateShareId(match._id);
        setMatches((prevMatches) =>
          prevMatches.map((m) =>
            m._id === match._id ? { ...m, shareId } : m
          )
        );
      }

      const shareUrl = `${window.location.origin}/share/${shareId}`;

      if (navigator.share && navigator.canShare && navigator.canShare({ url: shareUrl })) {
        try {
          await navigator.share({
            title: `${match.teamA} vs ${match.teamB} - Volleyball Match`,
            text: `Check out this volleyball match: ${match.teamA} ${match.scoreA} - ${match.scoreB} ${match.teamB}`,
            url: shareUrl
          });
          return;
        } catch (shareError) {
          console.warn('Web Share API failed, falling back to clipboard');
        }
      }

      const success = await copyToClipboard(shareUrl);
      if (success) {
        setCopiedMatchId(match._id);
        setTimeout(() => setCopiedMatchId(null), 2000);
      } else {
        prompt('Copy this URL to share the match:', shareUrl);
      }
    } catch (error) {
      console.error('Error sharing match:', error);
      try {
        const shareId = match.shareId || await generateShareId(match._id);
        const shareUrl = `${window.location.origin}/share/${shareId}`;
        prompt('Copy this URL to share the match:', shareUrl);
      } catch (finalError) {
        alert('Unable to generate share link. Please try again.');
      }
    }
  };

  const handlePublicToggle = async (match, isPublic) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/matches/${match._id}`,
        { isPublic },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMatches((prevMatches) =>
        prevMatches.map((m) =>
          m._id === match._id ? { ...m, isPublic } : m
        )
      );
    } catch (error) {
      console.error('Error updating match public status:', error);
      alert('Failed to update match visibility');
    }
  };

  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setOpponent('');
    setDateFrom('');
    setDateTo('');
    setResult('all');
    setSortBy('date');
    setSortOrder('desc');
    setTeamSuggestions([]);
  };

  const formatTime = (seconds) => {
    if (!seconds && seconds !== 0) return '';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m`;
    return `${remainingSeconds}s`;
  };

  const formatStamp = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const isMatchSelected = (match) => {
    return selectedMatches.some((selectedMatch) => selectedMatch._id === match._id);
  };

  const extraFilterCount = [opponent, dateFrom, dateTo].filter(Boolean).length;

  const renderActions = (match, id) => {
    const expanded = isExpanded(id);
    const selected = isMatchSelected(match);
    return (
      <div className="match-actions">
        {match.scoringOrder?.length > 0 && (
          <button
            type="button"
            className={`icon-btn ${expanded ? 'open' : ''}`}
            title={expanded ? 'Hide rally' : 'Rally'}
            aria-label={expanded ? 'Hide rally' : 'Show rally'}
            aria-expanded={expanded}
            onClick={(e) => toggleExpanded(id, e)}
          >
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        )}
        <button
          type="button"
          className={`icon-btn ${match.isPublic ? 'on' : ''}`}
          title={match.isPublic ? 'Public' : 'Make public'}
          aria-label={match.isPublic ? 'Make private' : 'Make public'}
          onClick={(e) => {
            e.stopPropagation();
            handlePublicToggle(match, !match.isPublic);
          }}
        >
          <Globe size={16} />
        </button>
        <button
          type="button"
          className="icon-btn"
          title="Edit"
          aria-label="Edit match"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(match);
          }}
        >
          <Pencil size={16} />
        </button>
        <button
          type="button"
          className="icon-btn"
          title="Share"
          aria-label="Share match"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleShare(match);
          }}
        >
          {copiedMatchId === match._id ? <Check size={16} /> : <Share size={16} />}
        </button>
        {onSelectMatch && (
          <button
            type="button"
            className={`icon-btn ${selected ? 'on' : ''}`}
            title={selected ? 'Deselect' : 'Select'}
            aria-label={selected ? 'Deselect match' : 'Select match'}
            onClick={(e) => {
              e.stopPropagation();
              onSelectMatch(match);
            }}
          >
            {selected ? <CheckSquare size={16} /> : <Square size={16} />}
          </button>
        )}
        {match._id && (
          <button
            type="button"
            className="icon-btn danger"
            title="Delete"
            aria-label="Delete match"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(match._id);
            }}
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>
    );
  };

  const renderScoringOrder = (scoringOrder) => {
    const runs = findScoringRuns(scoringOrder);
    const isInRun = (index) => runs.find((run) => index >= run.start && index <= run.end);

    const elements = [];
    let i = 0;

    while (i < scoringOrder.length) {
      const run = isInRun(i);

      if (run) {
        const runElements = [];
        for (let j = run.start; j <= run.end; j++) {
          const score = scoringOrder[j];
          runElements.push(
            <span
              key={j}
              className={rallyTickClass(score)}
              title={isAce(score) ? 'Ace serve' : undefined}
            >
              {scoringBoxLabel(score)}
            </span>
          );
        }

        elements.push(
          <span key={`run-${run.start}`} className={`scoring-run run-${run.team.toLowerCase()}`}>
            {runElements}
          </span>
        );

        i = run.end + 1;
      } else {
        const score = scoringOrder[i];
        elements.push(
          <span
            key={i}
            className={rallyTickClass(score)}
            title={isAce(score) ? 'Ace serve' : undefined}
          >
            {scoringBoxLabel(score)}
          </span>
        );
        i++;
      }
    }

    return elements;
  };

  const generateChartData = (match) => {
    let scoreDataA = 0;
    let scoreDataB = 0;
    return match.scoringOrder.map((team, index) => {
      if (isTeamAPoint(team)) scoreDataA++;
      if (isTeamBPoint(team)) scoreDataB++;
      const leadingTeam = scoreDataA > scoreDataB ? match.teamA : (scoreDataB > scoreDataA ? match.teamB : 'Tie');
      return {
        totalPoints: index + 1,
        [match.teamA]: scoreDataA,
        [match.teamB]: scoreDataB,
        pointDifferential: Math.abs(scoreDataA - scoreDataB),
        leadingTeam,
        barColor: leadingTeam === match.teamA ? TEAM_A_COLOR : (leadingTeam === match.teamB ? TEAM_B_COLOR : '#94a3b8')
      };
    });
  };

  const renderScoreProgressionChart = (match) => {
    const chartData = generateChartData(match);

    return (
      <div className="score-chart">
        <h4>Score progression</h4>
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart data={chartData} margin={{ top: 10, right: 15, left: 10, bottom: 30 }}>
            <XAxis
              dataKey="totalPoints"
              tick={{ fontSize: 10, fill: 'currentColor' }}
              label={{ value: 'Points', position: 'insideBottom', offset: -5, style: { fontSize: '10px' } }}
            />
            <YAxis
              yAxisId="left"
              tick={{ fontSize: 10, fill: 'currentColor' }}
              label={{ value: 'Score', angle: -90, position: 'insideLeft', style: { fontSize: '10px' } }}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 10, fill: 'currentColor' }}
              label={{ value: 'Diff', angle: 90, position: 'insideRight', style: { fontSize: '10px' } }}
            />
            <Tooltip
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  return (
                    <div className="custom-tooltip">
                      <p>{`Point ${label}`}</p>
                      <p style={{ color: TEAM_A_COLOR }}>{`${match.teamA}: ${data[match.teamA]}`}</p>
                      <p style={{ color: TEAM_B_COLOR }}>{`${match.teamB}: ${data[match.teamB]}`}</p>
                      <p>{`Diff: ${data.pointDifferential}`}</p>
                      <p className="tooltip-lead">{`Leading: ${data.leadingTeam}`}</p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }} iconSize={10} />
            <Bar yAxisId="right" dataKey="pointDifferential" opacity={0.7} name="Point Differential">
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.barColor} />
              ))}
            </Bar>
            <Line yAxisId="left" type="monotone" dataKey={match.teamA} stroke={TEAM_A_COLOR} strokeWidth={2.5} dot={false} name={match.teamA} />
            <Line yAxisId="left" type="monotone" dataKey={match.teamB} stroke={TEAM_B_COLOR} strokeWidth={2.5} dot={false} name={match.teamB} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const renderMatchCard = (match, id, { isLast = false, offline = false } = {}) => {
    const resultKind = matchResult(match);
    const expanded = isExpanded(id);
    const selected = isMatchSelected(match);
    const aWon = match.scoreA > match.scoreB;
    const bWon = match.scoreB > match.scoreA;
    const rotations = match.scoringOrder?.length > 0 ? getServeRotationStats(match.scoringOrder).rotations : 0;
    const stamp = formatStamp(offline ? match.savedAt : match.date);
    const duration = formatTime(match.time);
    const timeoutsA = match.timeoutsA || [false, false];
    const timeoutsB = match.timeoutsB || [false, false];

    const openMatch = () => {
      if (onMatchClick) onMatchClick(match);
    };

    return (
      <article
        key={id}
        ref={isLast ? lastMatchElementRef : null}
        className={`match-card ${resultKind}${selected ? ' selected' : ''}${expanded ? ' expanded' : ''}${offline ? ' offline-match' : ''}${match.status === 'failed' ? ' sync-failed' : ''}`}
      >
        {offline && (
          <div className={`offline-banner ${match.status === 'failed' ? 'failed' : match.synced ? 'synced' : 'pending'}`}>
            <CloudOff size={13} />
            {match.synced
              ? 'Saved offline · synced'
              : match.status === 'failed'
                ? `Sync failed${match.lastError ? ` — ${match.lastError}` : ''}`
                : 'Saved offline · waiting to sync'}
          </div>
        )}

        <button type="button" className="match-card-main" onClick={openMatch}>
          <span className={`result-mark ${resultKind}`} aria-label={resultKind}>
            {resultLetter(resultKind)}
          </span>
          <div className="match-sides">
            <div className={`side ${aWon ? 'won' : bWon ? 'lost' : 'tied'}`}>
              <span className="side-name">
                {match.teamA}
                <span className="timeout-pips" aria-hidden="true">
                  {timeoutsA.map((used, index) => (
                    <i key={index} className={used ? 'used' : ''} />
                  ))}
                </span>
              </span>
              <span className="side-score">{match.scoreA}</span>
            </div>
            <div className={`side ${bWon ? 'won' : aWon ? 'lost' : 'tied'}`}>
              <span className="side-name">
                {match.teamB}
                <span className="timeout-pips" aria-hidden="true">
                  {timeoutsB.map((used, index) => (
                    <i key={index} className={used ? 'used' : ''} />
                  ))}
                </span>
              </span>
              <span className="side-score">{match.scoreB}</span>
            </div>
          </div>
        </button>

        <div className="match-card-foot">
          <div className="match-chips">
            {stamp && <span>{stamp}</span>}
            {duration && <span>{duration}</span>}
            {rotations > 0 && <span>{rotations} rot</span>}
            {match.matchGroup?.name && <span>{match.matchGroup.name}</span>}
            {match.isPublic && <span className="chip-public">Public</span>}
          </div>
          {renderActions(match, id)}
        </div>

        {match.note && <p className="match-note">{match.note}</p>}

        {expanded && match.scoringOrder?.length > 0 && (
          <div className="match-expanded">
            <div className="rally-legend">
              <span className="legend-a">{match.teamA}</span>
              <span className="legend-b">{match.teamB}</span>
            </div>
            <div className="match-scoring-order">
              {renderScoringOrder(match.scoringOrder)}
            </div>
            {renderScoreProgressionChart(match)}
          </div>
        )}
      </article>
    );
  };

  const onlineGroups = groupByDay(matches, (match) => match.date);
  const lastMatchId = matches[matches.length - 1]?._id;
  const unsyncedCount = offlineMatches.filter((match) => !match.synced).length;
  const hasQuery = searchTerm || opponent || dateFrom || dateTo || result !== 'all';

  return (
    <div className={`match-display ${nightMode ? 'night-mode' : ''}`}>
      <div className="matches-toolbar">
        <div className={`matches-search ${searchTerm ? 'has-value' : ''}`}>
          <Search size={18} />
          <input
            type="search"
            placeholder="Search teams, notes…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            aria-label="Search matches"
          />
          {searchTerm && (
            <button type="button" className="search-clear" onClick={() => setSearchTerm('')} aria-label="Clear search">
              <X size={14} />
            </button>
          )}
          <button
            type="button"
            className={`filter-toggle ${showFilters ? 'open' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
            aria-expanded={showFilters}
          >
            <Filter size={16} />
            {extraFilterCount > 0 && <span className="filter-count">{extraFilterCount}</span>}
          </button>
        </div>

        <div className="matches-chip-row">
          <div className="result-chips" role="tablist" aria-label="Filter by result">
            {[
              { id: 'all', label: 'All' },
              { id: 'win', label: 'Wins' },
              { id: 'loss', label: 'Losses' },
              { id: 'tie', label: 'Ties' }
            ].map((chip) => (
              <button
                key={chip.id}
                type="button"
                role="tab"
                aria-selected={result === chip.id}
                className={`chip ${result === chip.id ? 'active' : ''}`}
                onClick={() => setResult(chip.id)}
              >
                {chip.label}
              </button>
            ))}
          </div>
          <div className="sort-chips">
            <button
              type="button"
              className={`chip ${sortBy === 'date' ? 'active' : ''}`}
              onClick={() => handleSortChange('date')}
            >
              <Calendar size={13} />
              Date
              {sortBy === 'date' && (sortOrder === 'desc' ? <SortDesc size={13} /> : <SortAsc size={13} />)}
            </button>
            <button
              type="button"
              className={`chip ${sortBy === 'scoreA' ? 'active' : ''}`}
              onClick={() => handleSortChange('scoreA')}
            >
              Score
              {sortBy === 'scoreA' && (sortOrder === 'desc' ? <SortDesc size={13} /> : <SortAsc size={13} />)}
            </button>
          </div>
        </div>
      </div>

      {showFilters && (
        <div className="filters-panel">
          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="opponent-filter">Opponent</label>
              <div className="input-with-suggestions">
                <input
                  id="opponent-filter"
                  type="text"
                  placeholder="Team name"
                  value={opponent}
                  onChange={(e) => {
                    setOpponent(e.target.value);
                    fetchTeamSuggestions(e.target.value);
                  }}
                />
                {teamSuggestions.length > 0 && (
                  <div className="suggestions">
                    {teamSuggestions.map((suggestion) => (
                      <button
                        type="button"
                        key={suggestion}
                        className="suggestion-item"
                        onClick={() => {
                          setOpponent(suggestion);
                          setTeamSuggestions([]);
                        }}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="filter-group">
              <label htmlFor="date-from">From</label>
              <input
                id="date-from"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>

            <div className="filter-group">
              <label htmlFor="date-to">To</label>
              <input
                id="date-to"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
          </div>

          <div className="filter-actions">
            <button type="button" onClick={clearFilters} className="clear-btn">
              Reset all
            </button>
          </div>
        </div>
      )}

      {offlineMatches.length > 0 && (
        <button
          type="button"
          className={`offline-toggle ${showOfflineMatches ? 'active' : ''}`}
          onClick={() => setShowOfflineMatches(!showOfflineMatches)}
        >
          <CloudOff size={15} />
          {unsyncedCount > 0
            ? `${unsyncedCount} unsynced ${unsyncedCount === 1 ? 'match' : 'matches'}`
            : `${offlineMatches.length} offline ${offlineMatches.length === 1 ? 'match' : 'matches'}`}
        </button>
      )}

      <div className="match-list">
        {showOfflineMatches && offlineMatches.length > 0 && (
          <section className="match-day">
            <h3 className="match-day-label">
              Offline
              <span>{offlineMatches.length}</span>
            </h3>
            {offlineMatches.map((match) => renderMatchCard(match, match.offlineId, { offline: true }))}
          </section>
        )}

        {isLoading && matches.length === 0 && (
          <div className="match-skeletons" aria-hidden="true">
            {[0, 1, 2, 3].map((slot) => (
              <div key={slot} className="match-skeleton" />
            ))}
          </div>
        )}

        {onlineGroups.map((group) => (
          <section key={group.key} className="match-day">
            <h3 className="match-day-label">
              {formatDayHeading(group.date)}
              <span>{group.matches.length}</span>
            </h3>
            {group.matches.map((match) =>
              renderMatchCard(match, match._id, { isLast: match._id === lastMatchId })
            )}
          </section>
        ))}

        {isLoading && matches.length > 0 && (
          <div className="loading-more">
            <span className="loading-spinner" />
            Loading more…
          </div>
        )}

        {!hasMore && matches.length > 0 && (
          <div className="end-message">That’s all of them</div>
        )}

        {matches.length === 0 && !isLoading && (
          <div className="empty-message">
            <Trophy size={36} />
            <p>{hasQuery ? 'No matches match those filters.' : 'No matches yet.'}</p>
            <span>{hasQuery ? 'Try a different team or date range.' : 'Finish a set on the Scoreboard to see it here.'}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default MatchDisplay;
